import React, { FC } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BaseStyle, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { useGamePolling } from '@nhl/react-hooks/lib/useGamePolling';

import { ApiResponseNHL } from '~/core/api';
import CommonMessages from '~/core/commonMessages';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { getEnvironmentAwareHref, parseValueFromLocalizedAttribute } from '~/core/utils';
import NoContent from '~/views/components/noContent/NoContent';
import PageWrapper from '~/views/components/pageWrapper/PageWrapper';
import SeriesPicker from '~/widgets/seriesPicker/SeriesPicker';

import { usePlayoffSeriesQuery } from './api';
import PlayoffSeriesScoreboard from './components/PlayoffSeriesScoreboard/PlayoffSeriesScoreboard';
import PlayoffSeriesTable from './components/PlayoffSeriesTable/PlayoffSeriesTable';
import { PlayoffSeries as TypePlayoffSeries } from './types';
import { getSeasonStringFromUrlYear, getSingleSeriesLetterFromUrlSeries, isSeriesComplete } from './utils';

import { PlayoffSeriesPicker, SeriesHeaderWrapper } from './styles';

const PlayoffSeries: FC = () => {
    const intl = useIntl();
    const isWebview = useIsWebview();

    const { year: yearStringFromUrl, seriesLetter: seriesLetterStringFromUrl } = useParams();
    const seriesLetter = getSingleSeriesLetterFromUrlSeries(seriesLetterStringFromUrl);

    const {
        data: playoffSeriesData,
        isLoading: isPlayoffSeriesResponseLoading,
        refetch,
    }: ApiResponseNHL<TypePlayoffSeries> = usePlayoffSeriesQuery({
        season: getSeasonStringFromUrlYear(yearStringFromUrl),
        seriesLetter,
    });

    useGamePolling(refetch, playoffSeriesData.games || []);

    if (isPlayoffSeriesResponseLoading) {
        return <LoadingDisplay isLoading={isPlayoffSeriesResponseLoading} />;
    }

    const { bottomSeedTeam, topSeedTeam, neededToWin } = playoffSeriesData;

    const bottomSeedTeamName = parseValueFromLocalizedAttribute(bottomSeedTeam.name);
    const topSeedTeamName = parseValueFromLocalizedAttribute(topSeedTeam.name);
    const areAllGamesNecessary = !playoffSeriesData.games.find(({ ifNecessary }) => ifNecessary);

    return (
        <PageWrapper
            defaultPageLayout
            hasExternalBackground
            theme={BaseStyle.DarkTheme}
            useSecondarySurfaceColorForBackground
        >
            <PlayoffSeriesPicker>
                <SeriesPicker
                    games={playoffSeriesData.games}
                    season={getSeasonStringFromUrlYear(yearStringFromUrl)}
                    language={intl.locale as ShortCodeEnums.Language}
                />
            </PlayoffSeriesPicker>
            <PlayoffSeriesScoreboard series={playoffSeriesData} year={yearStringFromUrl || ''} />
            <SeriesHeaderWrapper>
                <h3>
                    <FormattedMessage
                        description="Header of playoff series page describing which teams are playing"
                        defaultMessage="{bottomSeedTeamName} vs {topSeedTeamName}"
                        values={{ bottomSeedTeamName, topSeedTeamName }}
                    />
                </h3>
                {playoffSeriesData.fullCoverageUrl?.[intl.locale] && (
                    // note that I'm forcing English for the environment aware href - the full coverage url from the API
                    // will already have the language in it for the given locale, so I don't want any language code processing.
                    // This is mostly here for the isWebview portion.
                    <a
                        href={getEnvironmentAwareHref(
                            playoffSeriesData.fullCoverageUrl[intl.locale],
                            isWebview,
                            ShortCodeEnums.Language.English
                        )}
                    >
                        <FormattedMessage {...CommonMessages.misc.fullCoverageLink} />
                        &nbsp;
                        <ChevronRightIcon />
                    </a>
                )}
            </SeriesHeaderWrapper>
            {playoffSeriesData.games.length > 0 ? (
                <>
                    <PlayoffSeriesTable
                        data={playoffSeriesData.games}
                        bottomSeedTeam={bottomSeedTeam}
                        topSeedTeam={topSeedTeam}
                        neededToWin={neededToWin}
                    />
                    {!isSeriesComplete(bottomSeedTeam, topSeedTeam, neededToWin) && (
                        <p>
                            <FormattedMessage
                                description="Footnote indicating the games time and dates are subject to change."
                                defaultMessage="Subject to change"
                            />
                            {!areAllGamesNecessary && (
                                <>
                                    &nbsp;/&nbsp;
                                    <FormattedMessage
                                        description="Footnote saying that asterisk symbol indicates game will only be played if necessary"
                                        defaultMessage="* If necessary"
                                    />
                                </>
                            )}
                        </p>
                    )}
                </>
            ) : (
                <NoContent
                    primaryText={
                        <FormattedMessage
                            description="Message which describes a situation where a series does not have schedule games but will in the future."
                            defaultMessage="There are no games scheduled for this series yet."
                        />
                    }
                />
            )}
        </PageWrapper>
    );
};

export { PlayoffSeries as Component };
